import { Component } from "react";
import {
  arrayOf,
  bool,
  instanceOf,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";
import styled from "styled-components/macro";
import { rem } from "polished";

class Select extends Component {
  render() {
    const {
      defaultDisabled,
      defaultOption,
      inputRef,
      likeText,
      options,
      selected,
      size,
      ...rest
    } = this.props;

    return (
      <Input
        likeText={likeText}
        $placeholder={!!defaultOption && !selected}
        ref={inputRef}
        size={size}
        {...rest}
      >
        {!!defaultOption && (
          <option disabled={defaultDisabled} value="">
            {defaultOption}
          </option>
        )}
        {Array.isArray(options) &&
          !!options.length &&
          options.map(option => (
            <option
              key={option.value || option.path}
              value={option.value || option.path}
            >
              {!!option.label || option.label === 0
                ? option.label
                : option.value}
              {!!option.title ? option.title : option.path}
            </option>
          ))}
      </Input>
    );
  }
}

Select.propTypes = {
  defaultDisabled: bool,
  defaultOption: string,
  disabled: bool,
  inputRef: shape({
    current: instanceOf(Element),
  }),
  leftPad: string,
  likeText: bool,
  options: arrayOf(
    shape({
      label: string,
      value: oneOfType([number, string]),
      title: string,
      path: string,
    })
  ),
  size: string,
  value: oneOfType([number, string]),
  arrowType: string,
};

Select.defaultProps = {
  value: "",
  arrowType: "",
};

const caret = fill =>
  `data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2058%2033%22%3E%3Cpath%20d%3D%22M29%2032.12a5.9%205.9%200%200%201-4.2-1.74L2.14%207.71A4%204%200%200%201%207.8%202.06L29%2023.26l21.2-21.2a4%204%200%200%201%205.66%205.65L33.2%2030.38a5.9%205.9%200%200%201-4.2%201.74z%22%20fill%3D%22${encodeURIComponent(
    fill
  )}%22%2F%3E%3C%2Fsvg%3E`;

const Input = styled.select`
  appearance: none;
  background-color: ${({ likeText, theme }) =>
    likeText ? theme.text.backgroundColor : "transparent"};
  background-image: url(${({ theme, caretfill }) =>
    caretfill ? caret(caretfill) : caret(theme.select.caret)});
  background-position: right ${rem("13px")} center;
  background-repeat: no-repeat;
  background-size: ${({ arrowType }) => {
    switch (arrowType) {
      case "small":
        return `${rem("16px")} ${rem("8px")}`;
      case "smaller":
        return `${rem("10px")} ${rem("5px")}`;
      default:
        return `${rem("22px")} ${rem("12px")}`;
    }
  }};
  border-color: ${({ likeText, theme }) =>
    likeText ? theme.text.borderColor : theme.select.border};
  border-radius: ${({ likeText }) => (likeText ? rem("5px") : "0")};
  border-style: solid;
  border-width: ${({ likeText }) =>
    likeText ? rem("1px") : `0 0 ${rem("1px")}`};
  color: ${({ $placeholder, theme }) =>
    $placeholder ? theme.input.placeholder.color : theme.input.color};
  display: block;
  font-size: ${({ size, theme }) => {
    switch (size) {
      case "small":
        return rem("16px");
      default:
        return theme.input.fontSize;
    }
  }};
  height: ${({ likeText, theme }) =>
    likeText ? rem("45px") : theme.select.height};
  line-height: ${({ theme }) => theme.input.lineHeight};
  padding-bottom: ${({ theme }) => theme.input.paddingY};
  padding-left: ${({ leftPad, likeText }) => {
    switch (leftPad) {
      case "large":
        return likeText ? rem("14px") : rem("15px");
      case "small":
        return rem("7px");
      default:
        return 0;
    }
  }};
  padding-right: ${rem(13 * 2 + 22 + "px")};
  padding-top: ${({ theme }) => theme.input.paddingY};
  width: 100%;

  &:focus {
    outline: ${rem("1px")} dotted;
    outline-offset: ${rem("2px")};
  }

  &:disabled {
    color: ${({ likeText, theme }) =>
      likeText ? theme.input.placeholder.color : theme.select.disabled};
    opacity: 1;
  }

  .has-error & {
    border-color: ${({ theme }) => theme.error.color};
  }

  option {
    color: ${({ theme }) => theme.select.fallbackColor};
  }
`;

export default Select;
